import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";

// Components
import LoadingScreen from "./components/LoadingScreen";
import Header from "./components/Header";
import Hero from "./components/Hero";
import About from "./components/About";
import Services from "./components/Services";
import Clients from "./components/Clients";
import Partners from "./components/Partners";
import Certifications from "./components/Certifications";
import IndiaMap from "./components/IndiaMap";
import Testimonials from "./components/Testimonials";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import ChatBot from "./components/ChatBot";
import StickyContactBar from "./components/StickyContactBar";

// Scroll to Top Button Component with Animation
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility, { passive: true });
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          className="scroll-to-top visible"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          data-testid="scroll-to-top"
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.8 }}
          whileHover={{ y: -3, backgroundColor: '#3b82f6', color: '#fff' }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 15l-6-6-6 6"/>
          </svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isLoading]);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  return (
    <div className="App">
      {/* Liquid Retina Gradient Background */}
      <div className="liquid-retina-bg">
        <div className="retina-blob retina-1"></div>
        <div className="retina-blob retina-2"></div>
        <div className="retina-blob retina-3"></div>
        <div className="retina-noise"></div>
      </div>

      <AnimatePresence mode="wait">
        {isLoading && (
          <LoadingScreen key="loading" onLoadingComplete={handleLoadingComplete} />
        )}
      </AnimatePresence>
      
      {/* Header outside main-content for proper fixed positioning */}
      <AnimatePresence>
        {!isLoading && (
          <motion.div
            key="header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <Header />
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {!isLoading && (
          <motion.div 
            className="main-content visible"
            key="main"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {/* Draggable ChatBot - Left Side */}
            <ChatBot />
            
            <main>
              <Hero />
              <About />
              <Services />
              <Partners />
              <Clients />
              <Certifications />
              <IndiaMap />
              <Testimonials />
              <Contact />
            </main>
            <Footer />
            
            {/* Scroll to Top Button */}
            <ScrollToTop />
            
            {/* Sticky Contact Bar for Mobile */}
            <StickyContactBar />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
